import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Camera } from '@capacitor/camera';
import { Browser } from '@capacitor/browser';
import { App as CapacitorApp } from '@capacitor/app';
import { JigsawGame } from './game/JigsawGame';
import { PhotoManager } from './utils/PhotoManager';
import { ImageCompressor } from './utils/ImageCompressor';
import { ShareManager } from './utils/ShareManager';
import { DailyPuzzleManager } from './utils/DailyPuzzleManager';
import { dailyPuzzles, getDailyPuzzleByDate, getLocalPuzzleByDate, DailyPuzzle } from './data/dailyPuzzles';
import { CollectionManager, PuzzleCollection } from './utils/CollectionManager';

// DEBUG FLAG: Set to true to force all puzzles to 3x3 grid
const FORCE_3X3_GRID = true;

// Platform type: 'web' or 'app'
const PLATFORM_TYPE = Capacitor.isNativePlatform() ? 'app' : 'web';

class App {
  private game: JigsawGame | null = null;
  private currentCalendarMonth: number = new Date().getMonth() + 1; // Default to current month
  private currentCalendarYear: number = new Date().getFullYear(); // Default to current year
  private currentCollectionId: string | null = null;
  private currentPuzzleIndex: number = -1;
  private currentPuzzleServerId: string | null = null; // Server ID for shareable puzzles
  private currentDailyPuzzleDate: string | null = null; // Date for daily puzzles (yyyy-mm-dd)

  // Helper method to add both click and touch events for iOS compatibility
  private addClickHandler(element: HTMLElement, handler: (e?: Event) => void) {
    element.addEventListener('click', handler);

    // Track touch position to distinguish tap from scroll
    let touchStartY = 0;
    let touchStartX = 0;
    let touchStartTime = 0;

    element.addEventListener('touchstart', (e) => {
      touchStartY = e.touches[0].clientY;
      touchStartX = e.touches[0].clientX;
      touchStartTime = Date.now();
    }, { passive: true });

    element.addEventListener('touchend', (e) => {
      const touchEndY = e.changedTouches[0].clientY;
      const touchEndX = e.changedTouches[0].clientX;
      const touchEndTime = Date.now();

      // Calculate distance moved
      const deltaY = Math.abs(touchEndY - touchStartY);
      const deltaX = Math.abs(touchEndX - touchStartX);
      const deltaTime = touchEndTime - touchStartTime;

      // Only trigger if it's a tap (not a scroll)
      // Tap = moved less than 10px and took less than 500ms
      if (deltaY < 10 && deltaX < 10 && deltaTime < 500) {
        e.preventDefault();
        handler(e);
      }
    });
  }

  async initialize() {
    console.log('Initializing JoinSaw...');

    // Add native class to body for platform-specific styling
    if (Capacitor.isNativePlatform()) {
      document.body.classList.add('native');

      await StatusBar.setStyle({ style: Style.Dark });
      // setBackgroundColor is only available on Android
      if (Capacitor.getPlatform() === 'android') {
        await StatusBar.setBackgroundColor({ color: '#1a1a1a' });
      }

      // Setup deep link listener for URLs like joinsaw://login?login_auth=xxx
      CapacitorApp.addListener('appUrlOpen', (data) => {
        console.log('App opened with URL:', data.url);
        this.handleDeepLink(data.url);
      });
    }

    // Initialize game canvas but don't start yet
    const canvas = document.getElementById('game-canvas') as HTMLCanvasElement;
    if (canvas) {
      this.game = new JigsawGame(canvas);
    }

    this.setupControls();
    this.setupFrontPageControls();

    // Check if a daily puzzle date was specified (from window variable, URL path, or URL parameter)
    let dailyDate = (window as any).DAILY_PUZZLE_DATE;
    if (!dailyDate) {
      // Check for path-based URL: /daily/2025-12-26
      const pathMatch = window.location.pathname.match(/\/daily\/(\d{4}-\d{2}-\d{2})/);
      if (pathMatch) {
        dailyDate = pathMatch[1];
      } else {
        // Fallback to query parameter: ?daily=2025-12-26
        const urlParams = new URLSearchParams(window.location.search);
        dailyDate = urlParams.get('daily');
      }
    }

    if (dailyDate && dailyDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
      console.log('Auto-loading daily puzzle:', dailyDate);
      await this.loadDailyPuzzle(dailyDate);
      // Don't show front page since we're loading a puzzle
    } else {
      // Show front page initially only if no puzzle is being loaded
      this.showFrontPage();
    }

    const loading = document.getElementById('loading');
    if (loading) {
      loading.style.display = 'none';
    }

    console.log('JoinSaw initialized successfully');
  }

  private setupControls() {
    // Footer controls (for native platforms)
    const newGameBtnFooter = document.getElementById('new-game-footer');
    const pickPhotoBtnFooter = document.getElementById('pick-photo-footer');
    const gridSizeSelectFooter = document.getElementById('grid-size-footer') as HTMLSelectElement;

    // Setup footer controls (native)
    if (gridSizeSelectFooter) {
      gridSizeSelectFooter.addEventListener('change', () => {
        const size = parseInt(gridSizeSelectFooter.value);
        console.log('Grid size changed to', size);
        this.game?.setGridSize(size);
        this.game?.startNewGame();
      });
    }

    if (newGameBtnFooter) {
      newGameBtnFooter.addEventListener('click', async () => {
        console.log('New game clicked');
        if (gridSizeSelectFooter) {
          const size = parseInt(gridSizeSelectFooter.value);
          this.game?.setGridSize(size);
        }
        await this.startNewGameWithSavedPhotos();
      });
    }

    if (pickPhotoBtnFooter) {
      pickPhotoBtnFooter.addEventListener('click', () => {
        this.showPhotoModal();
      });
    }

    // Setup share buttons
    const shareBtn = document.getElementById('share-btn');
    const shareBtnFooter = document.getElementById('share-btn-footer');

    if (shareBtn) {
      this.addClickHandler(shareBtn as HTMLElement, () => {
        this.showShareModal();
      });
    }

    if (shareBtnFooter) {
      this.addClickHandler(shareBtnFooter as HTMLElement, () => {
        this.showShareModal();
      });
    }

    // Setup modal controls
    const closeModalBtn = document.getElementById('close-modal');
    const pickNewPhotoBtn = document.getElementById('pick-new-photo');

    if (closeModalBtn) {
      this.addClickHandler(closeModalBtn as HTMLElement, () => {
        this.hidePhotoModal();
      });
    }

    if (pickNewPhotoBtn) {
      this.addClickHandler(pickNewPhotoBtn as HTMLElement, async () => {
        await this.pickPhoto();
      });
    }

    // Setup share modal controls
    const closeShareModalBtn = document.getElementById('close-share-modal');
    if (closeShareModalBtn) {
      this.addClickHandler(closeShareModalBtn as HTMLElement, () => {
        this.hideShareModal();
      });
    }

    const shareOptions = document.querySelectorAll('.share-option');
    shareOptions.forEach(btn => {
      this.addClickHandler(btn as HTMLElement, async (e) => {
        const target = e?.currentTarget as HTMLElement;
        const shareType = target.getAttribute('data-share-type');
        await this.handleShare(shareType);
      });
    });
  }

  private async startNewGameWithSavedPhotos() {
    // Get a random image from default images or user photos
    const imageUrl = PhotoManager.getRandomImageUrl();
    await this.game?.startNewGame(true, imageUrl);
  }

  private showPhotoModal() {
    const modal = document.getElementById('photo-modal');
    if (modal) {
      modal.classList.add('show');
      this.renderPhotoGallery();
    }
  }

  private hidePhotoModal() {
    const modal = document.getElementById('photo-modal');
    if (modal) {
      modal.classList.remove('show');
    }
  }

  private async showShareModal() {
    const modal = document.getElementById('share-modal');
    const urlDisplay = document.getElementById('share-url-display');
    const copyMessage = document.getElementById('copy-message');
    const copyIcon = document.getElementById('copy-icon');
    const thumbnail = document.getElementById('share-thumbnail') as HTMLImageElement;

    if (modal && urlDisplay) {
      // Reset copy message and icon
      if (copyMessage) {
        copyMessage.style.opacity = '0';
      }
      if (copyIcon) {
        copyIcon.textContent = '📋';
      }

      // Set the thumbnail image
      if (thumbnail && this.game) {
        const imageDataUrl = this.game.getCurrentImageDataUrl();
        if (imageDataUrl) {
          thumbnail.src = imageDataUrl;
          thumbnail.style.display = 'block';
        } else {
          thumbnail.style.display = 'none';
        }
      }

      // Generate the share URL
      const shareUrl = await ShareManager.generateShareUrl(this.currentPuzzleServerId, this.currentDailyPuzzleDate || undefined);

      // Display the URL (or message if not shareable)
      urlDisplay.textContent = shareUrl;
      urlDisplay.style.display = 'block';

      // Show the modal
      (modal as HTMLElement).style.display = 'flex';
    }
  }

  private hideShareModal() {
    const modal = document.getElementById('share-modal');
    if (modal) {
      (modal as HTMLElement).style.display = 'none';
    }
  }

  private async handleShare(shareType: string | null) {
    const urlDisplay = document.getElementById('share-url-display');
    const shareUrl = urlDisplay?.textContent || '';

    if (!shareUrl) {
      console.error('No share URL available');
      return;
    }

    try {
      switch (shareType) {
        case 'copy':
          const copied = await ShareManager.copyToClipboard(shareUrl);
          if (copied) {
            // Show success message
            const copyMessage = document.getElementById('copy-message');
            const copyIcon = document.getElementById('copy-icon');

            if (copyMessage) {
              copyMessage.style.opacity = '1';

              // Fade out after 2 seconds
              setTimeout(() => {
                copyMessage.style.opacity = '0';
              }, 2000);
            }

            // Add checkmark to button
            if (copyIcon) {
              copyIcon.textContent = '✓';

              // Reset icon after 3 seconds
              setTimeout(() => {
                copyIcon.textContent = '📋';
              }, 3000);
            }
          } else {
            alert('Failed to copy link. Please try again.');
          }
          break;
        case 'sms':
          await ShareManager.shareViaSMS(shareUrl);
          break;
        case 'email':
          await ShareManager.shareViaEmail(shareUrl);
          break;
        case 'facebook':
          await ShareManager.shareViaFacebook(shareUrl);
          break;
        default:
          console.warn('Unknown share type:', shareType);
      }

      // Close the modal after sharing (except for copy)
      if (shareType !== 'copy') {
        this.hideShareModal();
      }
    } catch (error) {
      console.error('Error sharing:', error);
      alert('Failed to share. Please try again.');
    }
  }

  private async renderPhotoGallery() {
    const gallery = document.getElementById('photo-gallery');
    const emptyMessage = document.getElementById('photo-empty');
    if (!gallery || !emptyMessage) return;

    const photos = PhotoManager.getPhotos();

    if (photos.length === 0) {
      gallery.innerHTML = '';
      emptyMessage.style.display = 'block';
      return;
    }

    emptyMessage.style.display = 'none';

    // Create photo grid with thumbnails
    gallery.innerHTML = photos.map(photo => `
      <div class="photo-item" data-photo-id="${photo.id}">
        <img src="${photo.dataUrl}" data-photo-id="${photo.id}" alt="Photo" onerror="this.parentElement.style.display='none';">
        <button class="photo-item-delete" data-photo-id="${photo.id}">×</button>
      </div>
    `).join('');

    // Clean up photos with broken images after a short delay
    setTimeout(() => {
      const brokenPhotos: string[] = [];
      gallery.querySelectorAll('.photo-item').forEach(item => {
        if ((item as HTMLElement).style.display === 'none') {
          const photoId = item.getAttribute('data-photo-id');
          if (photoId) brokenPhotos.push(photoId);
        }
      });

      // Remove broken photos from storage
      if (brokenPhotos.length > 0) {
        console.log('Removing broken photo references:', brokenPhotos.length);
        brokenPhotos.forEach(id => PhotoManager.deletePhoto(id));
      }
    }, 1000);

    // Add click handlers for photo selection
    const photoItems = gallery.querySelectorAll('.photo-item');
    photoItems.forEach(item => {
      const photoElement = item as HTMLElement;

      photoElement.addEventListener('click', async (e) => {
        const target = e.target as HTMLElement;
        // Don't trigger if clicking delete button
        if (target.classList.contains('photo-item-delete')) return;

        const photoId = photoElement.getAttribute('data-photo-id');
        const photo = photos.find(p => p.id === photoId);
        if (photo && photo.dataUrl) {
          // Show game view and load the custom image
          this.showGameView();
          this.hidePhotoModal();

          // Show loading indicator
          const loading = document.getElementById('loading');
          if (loading) {
            loading.style.display = 'block';
          }

          // Clear any daily puzzle tracking
          DailyPuzzleManager.clearCurrentPuzzle();

          await this.game?.loadCustomImage(photo.dataUrl);

          // Hide loading indicator
          if (loading) {
            loading.style.display = 'none';
          }

          // Hide next puzzle button for custom photos
          this.updateNextPuzzleButton(false);
        }
      });

      // Add long press for mobile to show delete button
      if (Capacitor.isNativePlatform()) {
        let longPressTimer: number | null = null;
        let touchStartX = 0;
        let touchStartY = 0;
        const deleteBtn = photoElement.querySelector('.photo-item-delete') as HTMLElement;

        photoElement.addEventListener('touchstart', (e) => {
          const touchEvent = e as TouchEvent;
          touchStartX = touchEvent.touches[0].clientX;
          touchStartY = touchEvent.touches[0].clientY;

          // Start long press timer (500ms)
          longPressTimer = window.setTimeout(() => {
            // Show delete button on long press
            if (deleteBtn) {
              deleteBtn.style.opacity = '1';
              // Add slight vibration feedback if available
              if ('vibrate' in navigator) {
                navigator.vibrate(50);
              }
            }
          }, 500);
        }, { passive: true });

        photoElement.addEventListener('touchmove', (e) => {
          // Cancel long press if finger moves too much
          const touchEvent = e as TouchEvent;
          const touchX = touchEvent.touches[0].clientX;
          const touchY = touchEvent.touches[0].clientY;
          const deltaX = Math.abs(touchX - touchStartX);
          const deltaY = Math.abs(touchY - touchStartY);

          if (deltaX > 10 || deltaY > 10) {
            if (longPressTimer !== null) {
              window.clearTimeout(longPressTimer);
              longPressTimer = null;
            }
          }
        }, { passive: true });

        photoElement.addEventListener('touchend', () => {
          // Cancel long press timer if touch ends before timeout
          if (longPressTimer !== null) {
            window.clearTimeout(longPressTimer);
            longPressTimer = null;
          }
        });

        photoElement.addEventListener('touchcancel', () => {
          if (longPressTimer !== null) {
            window.clearTimeout(longPressTimer);
            longPressTimer = null;
          }
        });
      }
    });

    // Add delete handlers
    const deleteButtons = gallery.querySelectorAll('.photo-item-delete');
    deleteButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const photoId = btn.getAttribute('data-photo-id');
        if (photoId && confirm('Delete this photo?')) {
          PhotoManager.deletePhoto(photoId);
          this.renderPhotoGallery();
        }
      });
    });

    // On mobile, hide all delete buttons when tapping on the gallery background
    if (Capacitor.isNativePlatform() && gallery) {
      gallery.addEventListener('touchstart', (e) => {
        // Only hide if tapping the gallery itself, not a photo item or delete button
        const target = e.target as HTMLElement;
        if (target.id === 'photo-gallery') {
          const allDeleteBtns = gallery.querySelectorAll('.photo-item-delete') as NodeListOf<HTMLElement>;
          allDeleteBtns.forEach(btn => {
            btn.style.opacity = '0';
          });
        }
      }, { passive: true });
    }

    // Scroll to top to show newly added photos
    gallery.scrollTop = 0;
  }

  private async pickPhoto() {
    console.log('Pick photo clicked, platform:', Capacitor.getPlatform());

    // Use native picker on mobile, file input on web
    if (Capacitor.isNativePlatform()) {
      try {
        const images = await Camera.pickImages({
          quality: 90,
          limit: 10  // Max 10 images per selection
        });

        let processedCount = 0;
        for (const image of images.photos) {
          // Use the path from the photo library directly - no need to copy
          // On iOS: file:/// URI pointing to photo library
          // On Android: content:// URI pointing to MediaStore
          const photoPath = image.path || image.webPath;
          if (photoPath) {
            const saved = PhotoManager.addPhoto(photoPath);
            if (saved !== null) {
              processedCount++;
            }
          }
        }

        if (processedCount === 0) {
          alert('Storage is full! Please delete some photos to add new ones.');
        } else if (processedCount < images.photos.length) {
          alert(`Added ${processedCount} of ${images.photos.length} photos. Storage may be full.`);
        }
        // All photos added successfully - no alert needed

        this.renderPhotoGallery();  // Single refresh after batch
      } catch (error) {
        console.error('Error picking photo', error);
      }
    } else {
      // Browser fallback - use file input with multiple selection
      console.log('Using browser file input');
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.multiple = true; // Allow multiple file selection

      input.onchange = async (e) => {
        const files = (e.target as HTMLInputElement).files;
        if (files && files.length > 0) {
          console.log(`${files.length} file(s) selected`);

          let processedCount = 0;
          let failedCount = 0;

          // Read all files
          for (let i = 0; i < files.length; i++) {
            const file = files[i];
            console.log('Processing file:', file.name);

            const reader = new FileReader();
            reader.onload = async (event) => {
              const dataUrl = event.target?.result as string;
              if (dataUrl) {
                // Compress image before saving
                const compressedDataUrl = await ImageCompressor.compress(dataUrl, 1600, 1600, 0.9);
                // Save to photo library
                const saved = PhotoManager.addPhoto(compressedDataUrl);
                if (saved === null) {
                  failedCount++;
                }
              }

              processedCount++;

              // When all files are processed, refresh the gallery and show errors if any
              if (processedCount === files.length) {
                const successCount = files.length - failedCount;
                console.log(`${successCount} of ${files.length} photo(s) added to library`);

                if (failedCount > 0) {
                  alert(`Storage is full! ${failedCount} photo(s) could not be added. Please delete some photos to free up space.`);
                }

                // Refresh gallery to show all new photos that were successfully added
                this.renderPhotoGallery();
              }
            };
            reader.readAsDataURL(file);
          }
        }
      };

      input.click();
    }
  }

  // Front Page & Navigation Methods
  private setupFrontPageControls() {
    const dailyPuzzleBtn = document.getElementById('daily-puzzle-btn');
    const sharedPuzzlesBtn = document.getElementById('shared-puzzles-btn');
    const loginBtn = document.getElementById('login-btn');
    const backToMenuBtn = document.getElementById('back-to-menu');
    const closeCalendarBtn = document.getElementById('close-calendar');
    const nextPuzzleBtn = document.getElementById('next-puzzle-btn');
    const remixPuzzleBtn = document.getElementById('remix-puzzle-btn');
    const prevMonthBtn = document.getElementById('prev-month-btn');
    const nextMonthBtn = document.getElementById('next-month-btn');

    if (dailyPuzzleBtn) {
      const showCalendar = () => {
        console.log('Daily puzzle button clicked');
        this.showCalendarModal();
      };
      dailyPuzzleBtn.addEventListener('click', showCalendar);
      dailyPuzzleBtn.addEventListener('touchend', (e) => {
        e.preventDefault();
        showCalendar();
      });
    }

    if (sharedPuzzlesBtn) {
      const showCollections = () => {
        console.log('Shared puzzles button clicked');
        this.showCollectionsModal();
      };
      sharedPuzzlesBtn.addEventListener('click', showCollections);
      sharedPuzzlesBtn.addEventListener('touchend', (e) => {
        e.preventDefault();
        showCollections();
      });
    }

    if (loginBtn) {
      // Hide login button on web platform
      if (PLATFORM_TYPE === 'web') {
        loginBtn.style.display = 'none';
      } else {
        const openLogin = async () => {
          console.log('Login button clicked');

          // Generate random state and store it for verification
          const state = this.generateRandomState();
          sessionStorage.setItem('joinsaw_login_state', state);

          // Pass state to website for security verification
          await Browser.open({ url: `https://joinsaw.com/Account/login?successUri=app&state=${state}` });
        };
        loginBtn.addEventListener('click', openLogin);
        loginBtn.addEventListener('touchend', (e) => {
          e.preventDefault();
          openLogin();
        });
      }
    }

    if (backToMenuBtn) {
      this.addClickHandler(backToMenuBtn as HTMLElement, () => {
        const currentPuzzle = DailyPuzzleManager.getCurrentPuzzle();

        // If we're in a daily puzzle, go back to the calendar
        if (currentPuzzle?.type === 'daily') {
          // Clear the canvas before going back
          const canvas = document.getElementById('game-canvas') as HTMLCanvasElement;
          if (canvas) {
            const ctx = canvas.getContext('2d');
            if (ctx) {
              ctx.fillStyle = '#1a1a1a';
              ctx.fillRect(0, 0, canvas.width, canvas.height);
            }
          }

          const gameView = document.getElementById('game-view');
          if (gameView) gameView.style.display = 'none';
          this.showCalendarModal();
        }
        // If we're in a shared puzzle, go back to the collection
        else if (currentPuzzle?.type === 'shared' && this.currentCollectionId) {
          this.showGameView(); // Ensure game view is hidden
          const frontPage = document.getElementById('front-page');
          const gameView = document.getElementById('game-view');
          if (frontPage) frontPage.style.display = 'none';
          if (gameView) gameView.style.display = 'none';

          // Show the collection detail modal
          this.showCollectionDetailModal(this.currentCollectionId);
        } else {
          // Otherwise go back to front page
          this.showFrontPage();
        }
      });
    }

    if (closeCalendarBtn) {
      this.addClickHandler(closeCalendarBtn as HTMLElement, () => {
        this.hideCalendarModal();
      });
    }

    if (nextPuzzleBtn) {
      this.addClickHandler(nextPuzzleBtn as HTMLElement, async () => {
        const currentPuzzle = DailyPuzzleManager.getCurrentPuzzle();

        if (currentPuzzle?.type === 'shared') {
          // Load next shared puzzle in collection
          this.loadNextSharedPuzzle();
        } else if (currentPuzzle?.type === 'daily') {
          // Load next daily puzzle
          const nextPuzzle = this.getNextUncompletedPuzzle();
          if (nextPuzzle) {
            await this.loadDailyPuzzle(nextPuzzle.date);
          } else {
            // No more daily puzzles available
            alert('No more daily puzzles to do until tomorrow. Great work!');
            this.showFrontPage();
          }
        }
      });
    }

    if (remixPuzzleBtn) {
      this.addClickHandler(remixPuzzleBtn as HTMLElement, async () => {
        // Reshuffle the current puzzle
        if (this.game) {
          this.game.shufflePieces();
          // Hide the remix button after reshuffling
          this.updateRemixPuzzleButton(false);
        }
      });
    }

    if (prevMonthBtn) {
      this.addClickHandler(prevMonthBtn as HTMLElement, () => {
        this.changeCalendarMonth(-1);
      });
    }

    if (nextMonthBtn) {
      this.addClickHandler(nextMonthBtn as HTMLElement, () => {
        this.changeCalendarMonth(1);
      });
    }

    // Collections Modal controls
    const closeCollectionsBtn = document.getElementById('close-collections');
    const createCollectionBtn = document.getElementById('create-collection-btn');
    const closeCollectionDetailBtn = document.getElementById('close-collection-detail');
    const addPuzzleToCollectionBtn = document.getElementById('add-puzzle-to-collection-btn');
    const saveCollectionBtn = document.getElementById('save-collection-btn');
    const cancelCollectionBtn = document.getElementById('cancel-collection-btn');

    if (closeCollectionsBtn) {
      this.addClickHandler(closeCollectionsBtn as HTMLElement, () => {
        this.hideCollectionsModal();
      });
    }

    if (createCollectionBtn) {
      this.addClickHandler(createCollectionBtn as HTMLElement, () => {
        this.showCreateCollectionModal();
      });
    }

    if (closeCollectionDetailBtn) {
      this.addClickHandler(closeCollectionDetailBtn as HTMLElement, () => {
        // Make sure we show the front page first
        const frontPage = document.getElementById('front-page');
        const gameView = document.getElementById('game-view');
        if (frontPage) frontPage.style.display = 'flex';
        if (gameView) gameView.style.display = 'none';

        this.hideCollectionDetailModal();
        this.showCollectionsModal();
      });
    }

    if (addPuzzleToCollectionBtn) {
      this.addClickHandler(addPuzzleToCollectionBtn as HTMLElement, async () => {
        await this.addPhotosToCollection();
      });
    }

    if (saveCollectionBtn) {
      this.addClickHandler(saveCollectionBtn as HTMLElement, () => {
        this.createNewCollection();
      });
    }

    if (cancelCollectionBtn) {
      this.addClickHandler(cancelCollectionBtn as HTMLElement, () => {
        this.hideCreateCollectionModal();
      });
    }

    // Settings Modal controls
    const settingsBtn = document.getElementById('settings-btn');
    const closeSettingsBtn = document.getElementById('close-settings-btn');
    const clearProgressBtn = document.getElementById('clear-progress-btn');
    const clearAllDataBtn = document.getElementById('clear-all-data-btn');

    if (settingsBtn) {
      this.addClickHandler(settingsBtn as HTMLElement, () => {
        this.showSettingsModal();
      });
    }

    if (closeSettingsBtn) {
      this.addClickHandler(closeSettingsBtn as HTMLElement, () => {
        this.hideSettingsModal();
      });
    }

    if (clearProgressBtn) {
      this.addClickHandler(clearProgressBtn as HTMLElement, () => {
        this.clearProgress();
      });
    }

    if (clearAllDataBtn) {
      this.addClickHandler(clearAllDataBtn as HTMLElement, () => {
        this.clearAllData();
      });
    }

    const loginLogoutBtn = document.getElementById('login-logout-btn');
    if (loginLogoutBtn) {
      this.addClickHandler(loginLogoutBtn as HTMLElement, async () => {
        await this.handleLoginLogout();
      });
    }
  }

  private showFrontPage() {
    const frontPage = document.getElementById('front-page');
    const gameView = document.getElementById('game-view');

    if (frontPage) frontPage.style.display = 'flex';
    if (gameView) gameView.style.display = 'none';
  }

  private showGameView() {
    const frontPage = document.getElementById('front-page');
    const gameView = document.getElementById('game-view');

    if (frontPage) frontPage.style.display = 'none';
    if (gameView) gameView.style.display = 'flex';

    // Force canvas resize now that game view is visible
    if (this.game) {
      // Use setTimeout to ensure the layout has been applied
      setTimeout(() => {
        this.game?.handleResize();
      }, 0);
    }
  }

  private showCalendarModal() {
    const modal = document.getElementById('calendar-modal');
    if (modal) {
      // Force a fresh read of completed puzzles to ensure localStorage is synced
      console.log('Opening calendar modal');

      // Set button visibility before rendering
      const today = new Date();
      const currentMonth = today.getMonth() + 1;
      const currentYear = today.getFullYear();
      const isCurrentMonth = this.currentCalendarMonth === currentMonth && this.currentCalendarYear === currentYear;

      let prevMonth = this.currentCalendarMonth - 1;
      let prevYear = this.currentCalendarYear;
      if (prevMonth < 1) {
        prevMonth = 12;
        prevYear--;
      }

      const hasPreviousMonthPuzzles = dailyPuzzles.some(puzzle => {
        const puzzleDate = new Date(puzzle.date);
        return puzzleDate.getMonth() + 1 === prevMonth && puzzleDate.getFullYear() === prevYear;
      });

      const nextMonthBtn = document.getElementById('next-month-btn');
      const prevMonthBtn = document.getElementById('prev-month-btn');
      if (nextMonthBtn) {
        nextMonthBtn.style.visibility = isCurrentMonth ? 'hidden' : 'visible';
      }
      if (prevMonthBtn) {
        prevMonthBtn.style.visibility = hasPreviousMonthPuzzles ? 'visible' : 'hidden';
      }

      this.renderCalendar();
      (modal as HTMLElement).style.display = 'flex';
    }
  }

  private hideCalendarModal(showFrontPage: boolean = true) {
    const modal = document.getElementById('calendar-modal');
    if (modal) {
      (modal as HTMLElement).style.display = 'none';
    }
    // Show front page when closing calendar (unless loading a puzzle)
    if (showFrontPage) {
      this.showFrontPage();
    }
  }

  private changeCalendarMonth(delta: number) {
    // Calculate new month/year
    let newMonth = this.currentCalendarMonth + delta;
    let newYear = this.currentCalendarYear;

    // Wrap around months
    if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    } else if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    }

    // Calculate button visibility for the NEW month BEFORE rendering
    const today = new Date();
    const currentMonth = today.getMonth() + 1;
    const currentYear = today.getFullYear();
    const isCurrentMonth = newMonth === currentMonth && newYear === currentYear;

    let prevMonth = newMonth - 1;
    let prevYear = newYear;
    if (prevMonth < 1) {
      prevMonth = 12;
      prevYear--;
    }

    const hasPreviousMonthPuzzles = dailyPuzzles.some(puzzle => {
      const puzzleDate = new Date(puzzle.date);
      return puzzleDate.getMonth() + 1 === prevMonth && puzzleDate.getFullYear() === prevYear;
    });

    // Set button visibility BEFORE updating month variables
    const nextMonthBtn = document.getElementById('next-month-btn');
    const prevMonthBtn = document.getElementById('prev-month-btn');
    if (nextMonthBtn) {
      nextMonthBtn.style.visibility = isCurrentMonth ? 'hidden' : 'visible';
    }
    if (prevMonthBtn) {
      prevMonthBtn.style.visibility = hasPreviousMonthPuzzles ? 'visible' : 'hidden';
    }

    // Force a reflow to apply button visibility immediately
    if (nextMonthBtn) nextMonthBtn.offsetHeight;
    if (prevMonthBtn) prevMonthBtn.offsetHeight;

    // Now update the month/year
    this.currentCalendarMonth = newMonth;
    this.currentCalendarYear = newYear;

    // Render the calendar
    this.renderCalendar();
  }

  private renderCalendar() {
    const grid = document.getElementById('calendar-grid');
    const monthTitle = document.getElementById('calendar-month-title');
    if (!grid) return;

    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'];
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const completedPuzzles = DailyPuzzleManager.getCompletedPuzzles();
    console.log('Rendering calendar, completed puzzles:', completedPuzzles);
    const today = new Date();

    // Update month title
    if (monthTitle) {
      monthTitle.textContent = `${monthNames[this.currentCalendarMonth - 1]} ${this.currentCalendarYear}`;
    }

    // Add day headers
    let html = days.map(day =>
      `<div class="calendar-day-header">${day}</div>`
    ).join('');

    // Get first day of month and number of days
    const firstDay = new Date(this.currentCalendarYear, this.currentCalendarMonth - 1, 1);
    const firstDayOffset = firstDay.getDay();
    const daysInMonth = new Date(this.currentCalendarYear, this.currentCalendarMonth, 0).getDate();

    // Add empty cells for days before month start
    for (let i = 0; i < firstDayOffset; i++) {
      html += '<div class="calendar-day disabled"></div>';
    }

    // Get today's date string for comparison
    const todayString = today.toISOString().split('T')[0];

    // Add days
    for (let day = 1; day <= daysInMonth; day++) {
      const dateString = `${this.currentCalendarYear}-${String(this.currentCalendarMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const puzzle = getLocalPuzzleByDate(dateString);
      const isCompleted = puzzle && completedPuzzles.includes(puzzle.id);
      if (isCompleted) {
        console.log('Found completed puzzle:', dateString, puzzle.id);
      }
      const isAvailable = DailyPuzzleManager.isDateAvailable(dateString);
      const isFuture = !isAvailable;
      const isToday = dateString === todayString;

      let classes = 'calendar-day';
      // Only mark as future if the date is actually in the future
      if (isFuture) classes += ' future';
      if (isCompleted) classes += ' completed';
      if (isToday) classes += ' today';

      html += `
        <div class="${classes}" data-date="${dateString}">
          ${isCompleted ? '<span class="calendar-day-crown">✓</span>' : `<span class="calendar-day-number">${day}</span>`}
        </div>
      `;
    }

    grid.innerHTML = html;

    // Add touch event listeners to calendar days
    const calendarDays = grid.querySelectorAll('.calendar-day');
    calendarDays.forEach(day => {
      const dateString = day.getAttribute('data-date');
      if (!dateString) return;

      const isAvailable = DailyPuzzleManager.isDateAvailable(dateString);

      // Make dates clickable if they're available (past or today)
      // The API will be checked when the puzzle is loaded
      if (isAvailable) {
        this.addClickHandler(day as HTMLElement, async () => {
          // Add loading state to clicked day
          (day as HTMLElement).classList.add('loading');

          // On native platforms, load directly. On web, use URL navigation for proper routing
          if (Capacitor.isNativePlatform()) {
            await this.loadDailyPuzzle(dateString);
          } else {
            // Web: navigate to /daily/YYYY-MM-DD URL
            window.location.href = `/daily/${dateString}`;
          }
        });
      }
    });
  }

  private getNextUncompletedPuzzle(): DailyPuzzle | null {
    const currentPuzzle = DailyPuzzleManager.getCurrentPuzzle();
    if (!currentPuzzle || currentPuzzle.type !== 'daily') {
      return null;
    }

    const completedPuzzles = DailyPuzzleManager.getCompletedPuzzles();
    const availablePuzzles = dailyPuzzles.filter(p =>
      DailyPuzzleManager.isDateAvailable(p.date) && !completedPuzzles.includes(p.id)
    );

    if (availablePuzzles.length === 0) {
      return null;
    }

    // Get current puzzle date (IDs now use hyphens like "daily_2025-11-12")
    const currentDate = new Date(currentPuzzle.id.replace('daily_', ''));

    // First try to find next puzzle after current date
    const nextPuzzles = availablePuzzles
      .filter(p => new Date(p.date) > currentDate)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    if (nextPuzzles.length > 0) {
      return nextPuzzles[0];
    }

    // If no puzzles after, get the latest puzzle before current date
    const previousPuzzles = availablePuzzles
      .filter(p => new Date(p.date) < currentDate)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    if (previousPuzzles.length > 0) {
      return previousPuzzles[0];
    }

    return null;
  }

  private updateNextPuzzleButton(showAfterCompletion: boolean = false) {
    const nextPuzzleBtn = document.getElementById('next-puzzle-btn');
    if (!nextPuzzleBtn) return;

    const currentPuzzle = DailyPuzzleManager.getCurrentPuzzle();

    // Only show button for daily puzzles and after completion
    if (!currentPuzzle || currentPuzzle.type !== 'daily' || !showAfterCompletion) {
      nextPuzzleBtn.style.display = 'none';
      return;
    }

    // Check if there's a next puzzle
    const nextPuzzle = this.getNextUncompletedPuzzle();
    if (nextPuzzle) {
      nextPuzzleBtn.style.display = 'block';
    } else {
      nextPuzzleBtn.style.display = 'none';
    }
  }

  private updateRemixPuzzleButton(show: boolean) {
    const remixPuzzleBtn = document.getElementById('remix-puzzle-btn');
    if (!remixPuzzleBtn) return;

    remixPuzzleBtn.style.display = show ? 'block' : 'none';
  }

  async loadDailyPuzzle(dateString: string) {
    const puzzle = await getDailyPuzzleByDate(dateString);
    if (!puzzle) {
      console.error('Puzzle not found for date:', dateString);
      alert('No puzzle available for this date. Please try another date.');
      return;
    }

    // Validate puzzle has required imageUrl
    if (!puzzle.imageUrl) {
      console.error('Puzzle missing imageUrl:', puzzle);
      alert('This puzzle is missing image data. Please try another date.');
      return;
    }

    // Set the daily puzzle date for sharing
    this.currentPuzzleServerId = null;
    this.currentDailyPuzzleDate = dateString;

    // Set current puzzle
    DailyPuzzleManager.setCurrentPuzzle({
      type: 'daily',
      id: puzzle.id
    });

    // Check if this puzzle was already completed
    const isCompleted = DailyPuzzleManager.isPuzzleCompleted(puzzle.id);

    // Hide calendar without showing front page (we're loading the puzzle)
    this.hideCalendarModal(false);

    // Update the game header title
    const gameHeader = document.querySelector('#game-header h1');
    if (gameHeader) {
      gameHeader.textContent = dateString;
    }

    // Clear the canvas immediately and show game view
    const canvas = document.getElementById('game-canvas') as HTMLCanvasElement;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        // Clear the entire canvas to remove old puzzle
        ctx.fillStyle = '#1a1a1a';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
    }

    // Show game view (needed for canvas rendering)
    this.showGameView();

    // Show loading indicator
    const loading = document.getElementById('loading');
    if (loading) {
      loading.style.display = 'block';
    }

    // Set grid size and load image
    if (this.game) {
      const gridSize = FORCE_3X3_GRID ? 3 : puzzle.difficulty;
      this.game.setGridSize(gridSize);

      // Update grid size select (footer only)
      const gridSizeSelectFooter = document.getElementById('grid-size-footer') as HTMLSelectElement;
      if (gridSizeSelectFooter) gridSizeSelectFooter.value = String(gridSize);

      // Set completion callback for daily puzzles
      this.game.setOnCompleteCallback(() => {
        console.log('Daily puzzle completed:', puzzle.id);
        DailyPuzzleManager.markPuzzleCompleted(puzzle.id);
        // Hide remix button and show next puzzle button after completion
        this.updateRemixPuzzleButton(false);
        this.updateNextPuzzleButton(true);
      });

      // Load the puzzle image - don't shuffle if already completed
      try {
        await this.game.initialize(puzzle.imageUrl, !isCompleted);
      } catch (error) {
        console.error('Failed to load daily puzzle image:', error);
        alert('Failed to load the puzzle image. Please check your connection and try again.');

        // Navigate back to the calendar view
        if (loading) {
          loading.style.display = 'none';
        }
        this.showFrontPage();
        this.showCalendarModal();
        return;
      }

      // Hide loading indicator
      if (loading) {
        loading.style.display = 'none';
      }

      // If puzzle was already completed, show remix button instead of next button
      if (isCompleted) {
        this.updateRemixPuzzleButton(true);
        this.updateNextPuzzleButton(false);
      } else {
        this.updateRemixPuzzleButton(false);
        this.updateNextPuzzleButton(false);
      }
    }
  }

  // Collections Methods
  private showCollectionsModal() {
    const modal = document.getElementById('collections-modal');
    if (modal) {
      this.renderCollectionsGrid();
      (modal as HTMLElement).style.display = 'flex';
    }
  }

  private hideCollectionsModal() {
    const modal = document.getElementById('collections-modal');
    if (modal) {
      (modal as HTMLElement).style.display = 'none';
    }
  }

  private renderCollectionsGrid() {
    const grid = document.getElementById('collections-grid');
    if (!grid) return;

    const collections = CollectionManager.getCollections();

    if (collections.length === 0) {
      grid.innerHTML = '<div style="grid-column: 1 / -1; text-align: center; color: rgba(255, 255, 255, 0.6); padding: 40px;">No collections yet. Create your first one!</div>';
      return;
    }

    grid.innerHTML = collections.map(collection => {
      const puzzleCount = collection.puzzles.length;

      // Find the first completed puzzle in the collection
      const completedPuzzle = collection.puzzles.find(p =>
        DailyPuzzleManager.isSharedPuzzleCompleted(p.id)
      );

      // If there's a completed puzzle, show its thumbnail with overlay
      // Otherwise, show just the name and count
      const cardContent = completedPuzzle
        ? `
          <div style="aspect-ratio: 1; position: relative; overflow: hidden;">
            <img src="${completedPuzzle.imageUrl}" style="width: 100%; height: 100%; object-fit: cover;">
            <div style="position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0.6) 100%); display: flex; flex-direction: column; align-items: center; justify-content: center;">
              <div style="font-weight: 700; font-size: 20px; margin-bottom: 8px; text-shadow: 2px 2px 4px rgba(0,0,0,0.8);">${collection.name}</div>
              <div style="font-size: 14px; opacity: 0.9; text-shadow: 1px 1px 2px rgba(0,0,0,0.8);">${puzzleCount} puzzle${puzzleCount !== 1 ? 's' : ''}</div>
            </div>
          </div>
        `
        : `
          <div style="aspect-ratio: 1; background: linear-gradient(135deg, #22c55e 0%, #15803d 100%); display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 20px;">
            <div style="font-weight: 700; font-size: 20px; margin-bottom: 8px; text-align: center;">${collection.name}</div>
            <div style="font-size: 14px; opacity: 0.9;">${puzzleCount} puzzle${puzzleCount !== 1 ? 's' : ''}</div>
          </div>
        `;

      return `
        <div class="collection-item" data-collection-id="${collection.id}" style="position: relative; cursor: pointer; border-radius: 8px; overflow: hidden; background: rgba(255, 255, 255, 0.1); transition: all 0.2s ease;">
          ${cardContent}
        </div>
      `;
    }).join('');

    // Add click handlers
    const collectionItems = grid.querySelectorAll('.collection-item');
    collectionItems.forEach(item => {
      const handleClick = () => {
        const collectionId = item.getAttribute('data-collection-id');
        if (collectionId) {
          this.showCollectionDetailModal(collectionId);
        }
      };

      this.addClickHandler(item as HTMLElement, handleClick);

      // Add hover effect
      item.addEventListener('mouseenter', () => {
        (item as HTMLElement).style.background = 'rgba(255, 255, 255, 0.2)';
        (item as HTMLElement).style.transform = 'translateY(-2px)';
      });

      item.addEventListener('mouseleave', () => {
        (item as HTMLElement).style.background = 'rgba(255, 255, 255, 0.1)';
        (item as HTMLElement).style.transform = 'translateY(0)';
      });
    });
  }

  private showCollectionDetailModal(collectionId: string) {
    this.currentCollectionId = collectionId;
    const modal = document.getElementById('collection-detail-modal');
    const title = document.getElementById('collection-detail-title');

    if (modal && title) {
      const collection = CollectionManager.getCollectionById(collectionId);
      if (!collection) return;

      title.textContent = collection.name;
      this.renderCollectionPuzzles(collection);
      this.hideCollectionsModal();
      (modal as HTMLElement).style.display = 'flex';
    }
  }

  private hideCollectionDetailModal(clearCollectionId: boolean = true) {
    const modal = document.getElementById('collection-detail-modal');
    if (modal) {
      (modal as HTMLElement).style.display = 'none';
      // Only clear collection ID if explicitly requested (e.g., going back to menu)
      // Don't clear it when loading a puzzle, so we can show Next button
      if (clearCollectionId) {
        this.currentCollectionId = null;
      }
    }
  }

  private renderCollectionPuzzles(collection: PuzzleCollection) {
    const grid = document.getElementById('collection-puzzles-grid');
    const emptyMessage = document.getElementById('collection-empty');
    if (!grid || !emptyMessage) return;

    if (collection.puzzles.length === 0) {
      grid.innerHTML = '';
      emptyMessage.style.display = 'block';
      return;
    }

    emptyMessage.style.display = 'none';

    const greyGradient = 'linear-gradient(135deg, #636363 0%, #a2a2a2 100%)';

    grid.innerHTML = collection.puzzles.map((puzzle, index) => {
      const isCompleted = DailyPuzzleManager.isSharedPuzzleCompleted(puzzle.id);
      const imageContent = isCompleted
        ? `<img src="${puzzle.imageUrl}" style="width: 100%; height: 100%; object-fit: cover;" onerror="this.onerror=null; this.parentElement.innerHTML='<div style=\\'width: 100%; height: 100%; background: ${greyGradient}; display: flex; flex-direction: column; align-items: center; justify-content: center; font-size: 48px; color: rgba(255,255,255,0.7);\\'>⚠<div style=\\'font-size: 14px; margin-top: 10px;\\'>Photo deleted</div></div>'; this.parentElement.parentElement.setAttribute('data-broken', 'true');">`
        : `<div style="width: 100%; height: 100%; background: ${greyGradient}; display: flex; align-items: center; justify-content: center; font-size: 80px; font-weight: bold; color: #15803d; text-shadow: 2px 2px 4px rgba(0,0,0,0.3);">?</div>`;

      return `
        <div class="puzzle-item" data-puzzle-id="${puzzle.id}" data-puzzle-index="${index}" style="position: relative; cursor: pointer; border-radius: 8px; overflow: hidden; background: rgba(255, 255, 255, 0.1); transition: all 0.2s ease;">
          <div style="aspect-ratio: 1; overflow: hidden;">
            ${imageContent}
          </div>
          <div style="padding: 8px; text-align: center;">
            <div style="font-size: 14px; font-weight: 600; margin-bottom: 4px;">${puzzle.title}</div>
            <div style="font-size: 12px; opacity: 0.8;">${FORCE_3X3_GRID ? '3x3' : puzzle.difficulty + 'x' + puzzle.difficulty}</div>
          </div>
          ${!collection.isDefault ? `<button class="puzzle-delete-btn" data-puzzle-id="${puzzle.id}" style="position: absolute; top: 5px; right: 5px; background: rgba(255, 0, 0, 0.8); color: white; border: none; border-radius: 50%; width: 30px; height: 30px; font-size: 18px; cursor: pointer; display: flex; align-items: center; justify-content: center; opacity: 0; transition: opacity 0.2s;">×</button>` : ''}
        </div>
      `;
    }).join('');

    // Add click handlers for puzzles
    const puzzleItems = grid.querySelectorAll('.puzzle-item');
    puzzleItems.forEach(item => {
      const handlePuzzleClick = (e?: Event) => {
        const target = e?.target as HTMLElement;
        // Don't trigger if clicking delete button
        if (target && target.classList.contains('puzzle-delete-btn')) return;

        const puzzleId = item.getAttribute('data-puzzle-id');
        const puzzleIndex = parseInt(item.getAttribute('data-puzzle-index') || '0');
        if (puzzleId) {
          this.loadSharedPuzzle(puzzleId, puzzleIndex);
        }
      };

      this.addClickHandler(item as HTMLElement, handlePuzzleClick);

      // Add hover effects for desktop
      item.addEventListener('mouseenter', () => {
        (item as HTMLElement).style.background = 'rgba(255, 255, 255, 0.2)';
        (item as HTMLElement).style.transform = 'translateY(-2px)';
        const deleteBtn = item.querySelector('.puzzle-delete-btn') as HTMLElement;
        if (deleteBtn) deleteBtn.style.opacity = '1';
      });

      item.addEventListener('mouseleave', () => {
        (item as HTMLElement).style.background = 'rgba(255, 255, 255, 0.1)';
        (item as HTMLElement).style.transform = 'translateY(0)';
        const deleteBtn = item.querySelector('.puzzle-delete-btn') as HTMLElement;
        if (deleteBtn) deleteBtn.style.opacity = '0';
      });

      // Add long press for mobile to show delete button
      if (Capacitor.isNativePlatform()) {
        let longPressTimer: number | null = null;
        let touchStartX = 0;
        let touchStartY = 0;
        const deleteBtn = item.querySelector('.puzzle-delete-btn') as HTMLElement;

        item.addEventListener('touchstart', (e) => {
          const touchEvent = e as TouchEvent;
          touchStartX = touchEvent.touches[0].clientX;
          touchStartY = touchEvent.touches[0].clientY;

          // Start long press timer (500ms)
          longPressTimer = window.setTimeout(() => {
            // Show delete button on long press
            if (deleteBtn) {
              deleteBtn.style.opacity = '1';
              // Add slight vibration feedback if available
              if ('vibrate' in navigator) {
                navigator.vibrate(50);
              }
            }
          }, 500);
        }, { passive: true });

        item.addEventListener('touchmove', (e) => {
          // Cancel long press if finger moves too much
          const touchEvent = e as TouchEvent;
          const touchX = touchEvent.touches[0].clientX;
          const touchY = touchEvent.touches[0].clientY;
          const deltaX = Math.abs(touchX - touchStartX);
          const deltaY = Math.abs(touchY - touchStartY);

          if (deltaX > 10 || deltaY > 10) {
            if (longPressTimer !== null) {
              window.clearTimeout(longPressTimer);
              longPressTimer = null;
            }
          }
        }, { passive: true });

        item.addEventListener('touchend', () => {
          // Cancel long press timer if touch ends before timeout
          if (longPressTimer !== null) {
            window.clearTimeout(longPressTimer);
            longPressTimer = null;
          }
        });

        item.addEventListener('touchcancel', () => {
          if (longPressTimer !== null) {
            window.clearTimeout(longPressTimer);
            longPressTimer = null;
          }
        });
      }
    });

    // Add delete handlers
    const deleteButtons = grid.querySelectorAll('.puzzle-delete-btn');
    deleteButtons.forEach(btn => {
      const handleDelete = (e?: Event) => {
        e?.stopPropagation();
        const puzzleId = btn.getAttribute('data-puzzle-id');
        if (puzzleId && confirm('Delete this puzzle?')) {
          CollectionManager.deletePuzzleFromCollection(collection.id, puzzleId);

          // Refresh the view
          const updatedCollection = CollectionManager.getCollectionById(collection.id);
          if (updatedCollection) {
            this.renderCollectionPuzzles(updatedCollection);
          } else {
            // Collection was deleted (empty)
            this.hideCollectionDetailModal();
            this.showCollectionsModal();
          }
        }
      };

      this.addClickHandler(btn as HTMLElement, handleDelete);
    });

    // On mobile, hide all delete buttons when tapping on the grid background
    if (Capacitor.isNativePlatform() && grid) {
      grid.addEventListener('touchstart', (e) => {
        // Only hide if tapping the grid itself, not a puzzle item or delete button
        const target = e.target as HTMLElement;
        if (target.id === 'collection-puzzles-grid') {
          const allDeleteBtns = grid.querySelectorAll('.puzzle-delete-btn') as NodeListOf<HTMLElement>;
          allDeleteBtns.forEach(btn => {
            btn.style.opacity = '0';
          });
        }
      }, { passive: true });
    }
  }

  private showCreateCollectionModal() {
    const modal = document.getElementById('create-collection-modal');
    const input = document.getElementById('collection-name-input') as HTMLInputElement;

    if (modal && input) {
      input.value = '';
      (modal as HTMLElement).style.display = 'flex';
      setTimeout(() => input.focus(), 100);
    }
  }

  private hideCreateCollectionModal() {
    const modal = document.getElementById('create-collection-modal');
    if (modal) {
      (modal as HTMLElement).style.display = 'none';
    }
  }

  private createNewCollection() {
    const input = document.getElementById('collection-name-input') as HTMLInputElement;
    const name = input.value.trim();

    if (!name) {
      alert('Please enter a collection name');
      return;
    }

    const collection = CollectionManager.createCollection(name);
    if (!collection) {
      alert('Failed to create collection. Storage may be full.');
      return;
    }

    this.hideCreateCollectionModal();
    this.renderCollectionsGrid();
  }

  private async addPhotosToCollection() {
    if (!this.currentCollectionId) return;

    console.log('Add photos to collection:', this.currentCollectionId);

    // Use the same photo picker as before
    if (Capacitor.isNativePlatform()) {
      try {
        const images = await Camera.pickImages({
          quality: 90,
          limit: 10  // Max 10 images per selection
        });

        let processedCount = 0;
        for (const image of images.photos) {
          // Use the path from the photo library directly - no need to copy
          // On iOS: file:/// URI pointing to photo library
          // On Android: content:// URI pointing to MediaStore
          const photoPath = image.path || image.webPath;
          if (photoPath) {
            const title = `Puzzle ${Date.now()}`;
            const puzzle = await CollectionManager.addPuzzleToCollection(
              this.currentCollectionId,
              photoPath,
              title
            );

            if (puzzle) {
              processedCount++;
            }
          }
        }

        if (processedCount === 0) {
          alert('Failed to add puzzles. Storage may be full.');
        } else if (processedCount < images.photos.length) {
          alert(`Added ${processedCount} of ${images.photos.length} puzzles.`);
        }
        // All puzzles added successfully - no alert needed

        const collection = CollectionManager.getCollectionById(this.currentCollectionId);
        if (collection) {
          this.renderCollectionPuzzles(collection);
        }
      } catch (error) {
        console.error('Error picking photo', error);
      }
    } else {
      // Browser fallback
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.multiple = true;

      input.onchange = async (e) => {
        const files = (e.target as HTMLInputElement).files;
        if (files && files.length > 0 && this.currentCollectionId) {
          let processedCount = 0;
          let failedCount = 0;

          for (let i = 0; i < files.length; i++) {
            const file = files[i];
            const reader = new FileReader();
            reader.onload = async (event) => {
              const dataUrl = event.target?.result as string;
              if (dataUrl && this.currentCollectionId) {
                const compressedDataUrl = await ImageCompressor.compress(dataUrl, 1600, 1600, 0.9);
                const title = file.name.replace(/\.[^/.]+$/, ''); // Remove extension
                const puzzle = await CollectionManager.addPuzzleToCollection(this.currentCollectionId, compressedDataUrl, title);

                if (!puzzle) {
                  failedCount++;
                }
              }

              processedCount++;

              if (processedCount === files.length) {
                if (failedCount > 0) {
                  alert(`Storage is full! ${failedCount} photo(s) could not be added.`);
                }

                const collection = CollectionManager.getCollectionById(this.currentCollectionId!);
                if (collection) {
                  this.renderCollectionPuzzles(collection);
                }
              }
            };
            reader.readAsDataURL(file);
          }
        }
      };

      input.click();
    }
  }

  async loadSharedPuzzle(puzzleId: string, puzzleIndex: number = 0) {
    const puzzle = CollectionManager.getPuzzleById(puzzleId);
    if (!puzzle) {
      console.error('Puzzle not found:', puzzleId);
      return;
    }

    // Store the current puzzle index and server ID for sharing
    this.currentPuzzleIndex = puzzleIndex;
    this.currentPuzzleServerId = puzzle.serverId || null;
    this.currentDailyPuzzleDate = null; // Clear daily puzzle date

    // Set current puzzle
    DailyPuzzleManager.setCurrentPuzzle({
      type: 'shared',
      id: puzzle.id
    });

    // Check if this puzzle was already completed
    const isCompleted = DailyPuzzleManager.isSharedPuzzleCompleted(puzzle.id);

    // Hide collection detail modal and show game view
    // Don't clear collection ID so we can show Next button
    this.hideCollectionDetailModal(false);
    this.showGameView();

    // Clear canvas and show loading indicator
    const loading = document.getElementById('loading');
    const canvas = document.getElementById('game-canvas') as HTMLCanvasElement;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        // Fill with background color instead of just clearing to avoid visual artifacts
        ctx.fillStyle = '#1a1a1a';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
    }
    if (loading) {
      loading.style.display = 'block';
    }

    // Set grid size
    if (this.game) {
      const gridSize = FORCE_3X3_GRID ? 3 : puzzle.difficulty;
      this.game.setGridSize(gridSize);

      // Update grid size select (footer only)
      const gridSizeSelectFooter = document.getElementById('grid-size-footer') as HTMLSelectElement;
      if (gridSizeSelectFooter) gridSizeSelectFooter.value = String(gridSize);

      // Set completion callback for shared puzzles
      this.game.setOnCompleteCallback(() => {
        console.log('Shared puzzle completed:', puzzle.id);
        DailyPuzzleManager.markSharedPuzzleCompleted(puzzle.id);
        // Hide remix button and show next puzzle button after completion
        this.updateRemixPuzzleButton(false);
        this.updateNextPuzzleButtonForShared(true);
      });

      // Load the puzzle image - don't shuffle if already completed
      try {
        await this.game.initialize(puzzle.imageUrl, !isCompleted);
      } catch (error) {
        console.error('Failed to load puzzle image (photo may have been deleted):', error);
        // Delete the puzzle from the collection
        CollectionManager.deletePuzzleFromCollection(puzzle.collectionId, puzzle.id);
        alert('The photo for this puzzle has been deleted from your photo library. The puzzle has been removed.');

        // Navigate back to the collection view
        if (loading) {
          loading.style.display = 'none';
        }
        this.showFrontPage();
        if (this.currentCollectionId) {
          this.showCollectionDetailModal(this.currentCollectionId);
        } else {
          this.showCollectionsModal();
        }
        return;
      }

      // Hide loading indicator
      if (loading) {
        loading.style.display = 'none';
      }

      // If puzzle was already completed, show remix button
      if (isCompleted) {
        this.updateRemixPuzzleButton(true);
        this.updateNextPuzzleButtonForShared(false);
      } else {
        this.updateRemixPuzzleButton(false);
        this.updateNextPuzzleButtonForShared(false);
      }
    }
  }

  private updateNextPuzzleButtonForShared(showAfterCompletion: boolean = false) {
    console.log('updateNextPuzzleButtonForShared called, showAfterCompletion:', showAfterCompletion);
    const nextPuzzleBtn = document.getElementById('next-puzzle-btn');
    if (!nextPuzzleBtn) {
      console.log('Next puzzle button not found');
      return;
    }

    const currentPuzzle = DailyPuzzleManager.getCurrentPuzzle();
    console.log('Current puzzle:', currentPuzzle);

    // Only show button for shared puzzles and after completion
    if (!currentPuzzle || currentPuzzle.type !== 'shared' || !showAfterCompletion) {
      console.log('Hiding button - not shared or not after completion');
      nextPuzzleBtn.style.display = 'none';
      return;
    }

    // Check if there's a next puzzle in the collection
    if (!this.currentCollectionId) {
      console.log('Hiding button - no current collection ID');
      nextPuzzleBtn.style.display = 'none';
      return;
    }

    const collection = CollectionManager.getCollectionById(this.currentCollectionId);
    console.log('Collection:', collection, 'Current index:', this.currentPuzzleIndex);

    if (!collection || this.currentPuzzleIndex >= collection.puzzles.length - 1) {
      console.log('Hiding button - no collection or last puzzle');
      nextPuzzleBtn.style.display = 'none';
      return;
    }

    // There is a next puzzle, show the button
    console.log('Showing next puzzle button!');
    nextPuzzleBtn.style.display = 'block';
    nextPuzzleBtn.textContent = 'Next Puzzle';
  }

  private loadNextSharedPuzzle() {
    if (!this.currentCollectionId) return;

    const collection = CollectionManager.getCollectionById(this.currentCollectionId);
    if (!collection) return;

    const nextIndex = this.currentPuzzleIndex + 1;
    if (nextIndex >= collection.puzzles.length) return;

    const nextPuzzle = collection.puzzles[nextIndex];
    this.loadSharedPuzzle(nextPuzzle.id, nextIndex);
  }

  // Settings Methods
  private showSettingsModal() {
    const modal = document.getElementById('settings-modal');
    if (modal) {
      this.updateLoginLogoutButton();
      (modal as HTMLElement).style.display = 'flex';
    }
  }

  private hideSettingsModal() {
    const modal = document.getElementById('settings-modal');
    if (modal) {
      (modal as HTMLElement).style.display = 'none';
    }
  }

  private clearProgress() {
    const confirmed = confirm('Are you sure you want to clear your puzzle completion progress? Your collections and photos will remain.');

    if (!confirmed) {
      return;
    }

    // Clear only completion progress data
    localStorage.removeItem('joinsaw_daily_completed');
    localStorage.removeItem('joinsaw_shared_completed');

    // Close settings modal
    this.hideSettingsModal();

    // Show success message
    alert('Puzzle completion progress has been cleared. The app will reload.');

    // Reload the page to refresh the UI
    window.location.reload();
  }

  private clearAllData() {
    const confirmed = confirm('Are you sure you want to clear all data? This will delete all completed puzzles, collections, and photos. This action cannot be undone.');

    if (!confirmed) {
      return;
    }

    // Clear all localStorage data
    const keysToRemove = [
      'joinsaw_daily_completed',
      'joinsaw_shared_completed',
      'joinsaw_current_puzzle',
      'joinsaw_collections',
      'joinsaw_photos'
    ];

    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });

    // Reinitialize default collection
    CollectionManager.getCollections();

    // Close settings modal
    this.hideSettingsModal();

    // Show success message
    alert('All data has been cleared successfully. The app will reload.');

    // Reload the page to reset everything
    window.location.reload();
  }

  // Deep Link Handler
  private handleDeepLink(url: string) {
    console.log('Handling deep link:', url);

    try {
      const urlObj = new URL(url);
      const path = urlObj.hostname; // e.g., 'login' from joinsaw://login
      const params = new URLSearchParams(urlObj.search);

      // Handle login deep link: joinsaw://login?state=xxx&login_auth=yyy
      if (path === 'login') {
        const state = params.get('state');
        const loginAuth = params.get('login_auth');

        // Verify state matches what we stored (prevents malicious deep links)
        const storedState = sessionStorage.getItem('joinsaw_login_state');

        if (!state || state !== storedState) {
          console.error('Invalid state parameter - possible security attack');
          alert('Login failed: Invalid security token. Please try again.');
          return;
        }

        // Clear the state (one-time use only)
        sessionStorage.removeItem('joinsaw_login_state');

        if (loginAuth) {
          console.log('Received login authentication:', loginAuth);
          // Store the auth token
          localStorage.setItem('joinsaw_auth_token', loginAuth);

          // Show success message
          alert('Successfully logged in!');

          // Navigate to front page
          this.showFrontPage();
        } else {
          console.warn('Login deep link missing login_auth parameter');
          alert('Login failed: Missing authentication token.');
        }
      } else if (path === 'logout') {
        // Handle logout deep link: joinsaw://logout?state=xxx
        const state = params.get('state');

        // Verify state matches what we stored (prevents malicious deep links)
        const storedState = sessionStorage.getItem('joinsaw_logout_state');

        if (!state || state !== storedState) {
          console.error('Invalid state parameter - possible security attack');
          alert('Logout failed: Invalid security token.');
          return;
        }

        // Clear the state (one-time use only)
        sessionStorage.removeItem('joinsaw_logout_state');

        console.log('Logout confirmed via deep link');
        // Token was already cleared before opening browser
        // Just show confirmation
        this.showFrontPage();
      } else {
        console.warn('Unknown deep link path:', path);
      }
    } catch (error) {
      console.error('Error parsing deep link URL:', error);
      alert('Login failed: Invalid link format.');
    }
  }

  // Generate random state for CSRF protection
  private generateRandomState(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  // Update login/logout button state based on authentication
  private updateLoginLogoutButton() {
    const section = document.getElementById('login-logout-section');
    const btn = document.getElementById('login-logout-btn');
    const description = document.getElementById('login-logout-description');

    if (!section || !btn || !description) return;

    // Only show on native platform
    if (PLATFORM_TYPE !== 'app') {
      section.style.display = 'none';
      return;
    }

    const authToken = localStorage.getItem('joinsaw_auth_token');
    const isLoggedIn = !!authToken;

    section.style.display = 'block';

    if (isLoggedIn) {
      btn.textContent = 'Logout';
      description.textContent = 'Sign out of your account.';
      btn.style.background = 'rgba(239, 68, 68, 0.8)';
      btn.style.borderColor = 'rgba(239, 68, 68, 1)';
    } else {
      btn.textContent = 'Login';
      description.textContent = 'Sign in to sync your progress.';
      btn.style.background = 'rgba(34, 197, 94, 0.8)';
      btn.style.borderColor = 'rgba(34, 197, 94, 1)';
    }
  }

  // Handle login/logout button click
  private async handleLoginLogout() {
    const authToken = localStorage.getItem('joinsaw_auth_token');
    const isLoggedIn = !!authToken;

    if (isLoggedIn) {
      // Handle logout
      const confirmed = confirm('Are you sure you want to logout?');
      if (!confirmed) return;

      // Generate random state and store it for verification
      const state = this.generateRandomState();
      sessionStorage.setItem('joinsaw_logout_state', state);

      // Clear the auth token
      localStorage.removeItem('joinsaw_auth_token');

      // Navigate to logout URL
      await Browser.open({ url: `https://joinsaw.com/Account/logout?successUri=app&state=${state}` });

      // Close settings modal and update UI
      this.hideSettingsModal();
      alert('Successfully logged out!');
    } else {
      // Handle login - use the same flow as the login button
      const state = this.generateRandomState();
      sessionStorage.setItem('joinsaw_login_state', state);

      await Browser.open({ url: `https://joinsaw.com/Account/login?successUri=app&state=${state}` });
    }
  }
}

// Make app instance available globally for onclick handlers
declare global {
  interface Window {
    app: App;
  }
}

const app = new App();
window.app = app;
app.initialize().catch(console.error);
